import { useId, type CSSProperties } from 'react';

/* ---------------- Charts: plain HTML/SVG, no library. Motion follows Adam's 10-29 reference: bars grow up from the
   baseline in a left-to-right wave, built from stacked segments; hovering (or focusing) a column lights a full-height
   band and pops its value. Everything stops under prefers-reduced-motion (caios-theme.css). */

export function BarChart({ data, format = (n) => n.toLocaleString(), label, height = 200, color = 'var(--cx-accent)', testid }: {
  data: { key: string; label: string; value: number; detail?: string | undefined }[]; format?: (n: number) => string; label: string; height?: number; color?: string; testid?: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const ticks = [max, max / 2, 0];
  return (
    <div className="cx-bars" role="img" aria-label={label} style={{ ['--h' as string]: `${height}px`, ['--c' as string]: color } as CSSProperties} data-testid={testid}>
      <div className="cx-bars-axis" aria-hidden="true">{ticks.map((t) => <span key={t}>{format(t)}</span>)}</div>
      <div className="cx-bars-plot">
        {ticks.map((t, i) => <i key={t} className="cx-bars-grid" style={{ bottom: `${100 - i * 50}%` }} aria-hidden="true" />)}
        {data.map((d, i) => (
          <div key={d.key} className="cx-bars-col" data-edge={i < 2 ? 'start' : i >= data.length - 2 ? 'end' : undefined} tabIndex={0} aria-label={`${d.label}: ${format(d.value)}`} data-testid={testid ? `${testid}-col` : undefined}>
            <div className="cx-bars-tip"><b>{d.label}</b>{format(d.value)}{d.detail && <span>{d.detail}</span>}</div>
            <div className="cx-bars-bar" style={{ height: `${(d.value / max) * 100}%`, ['--d' as string]: `${i * 60}ms` } as CSSProperties} />
            <span className="cx-bars-x">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A small trend line for a stat tile; it draws itself in as the tile lands. */
export function Spark({ values, color = 'var(--c, var(--cx-accent))', width = 84, height = 26 }: { values: number[]; color?: string; width?: number; height?: number }) {
  const id = useId();
  // A line needs a shape: with fewer than three active points it reads as a glitch, not a trend.
  if (values.filter((v) => v > 0).length < 3) return null;
  const max = Math.max(1, ...values), min = Math.min(0, ...values);
  const x = (i: number) => (i * width) / (values.length - 1);
  const y = (v: number) => 2 + (1 - (v - min) / (max - min || 1)) * (height - 4);
  const line = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="cx-spark" viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
      <defs><linearGradient id={id} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".22" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <path d={`${line} L${width},${height} L0,${height} Z`} fill={`url(#${id})`} className="cx-area" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" pathLength={1} className="cx-draw" />
    </svg>
  );
}

/** "+12% vs last month". Nothing when there is no earlier figure to compare with — a percentage of zero is not a fact. */
export function Delta({ now, before, against = 'last month', testid }: { now: number; before: number; against?: string; testid?: string }) {
  if (!before) return null;
  const pct = Math.round(((now - before) / before) * 100);
  const tone = pct > 0 ? 'good' : pct < 0 ? 'fire' : '';
  return <span className="cx-delta" data-tone={tone} data-testid={testid}>{pct > 0 ? '+' : ''}{pct}% <span>vs {against}</span></span>;
}

/* ---------------- Dates: one way to write a day and one way to write a moment, everywhere.
   "30 Oct" / "30 Oct, 9:30 AM" — no seconds; the year only when it isn't this year. */
type When = string | number | Date;
const yearIfOther = (d: Date) => (d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' as const });
export const formatDay = (v: When) => { const d = new Date(v); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...yearIfOther(d) }); };
export const formatWhen = (v: When) => { const d = new Date(v); return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', ...yearIfOther(d), hour: 'numeric', minute: '2-digit', hour12: true }); };

/** "Europe/Madrid" → "Madrid time"; UTC stays UTC. */
export const tzName = (z: string | null | undefined) => (!z ? '' : /^(UTC|GMT|Etc\/UTC)$/.test(z) ? 'UTC' : `${(z.split('/').pop() ?? z).replace(/_/g, ' ')} time`);

/** Text that came from a website with HTML entities left in ("&#8211;" → "–"). */
export const plain = (v: string | null | undefined) => (v ?? '').replace(/&(#x[0-9a-f]+|#\d+|amp|quot|apos|lt|gt|nbsp);/gi, (_, e: string) => {
  const k = e.toLowerCase();
  if (k[0] === '#') return String.fromCodePoint(k[1] === 'x' ? parseInt(k.slice(2), 16) : Number(k.slice(1)));
  return ({ amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: ' ' } as Record<string, string>)[k] ?? _;
});

/** "1 ticket", "3 tickets". Pass the plural when it isn't just +s. */
export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
/** A stored value as words: "not_requested" -> "Not requested". For chips and cells that show a status or kind. */
export const human = (v: string | null | undefined) => (v ? (v.charAt(0).toUpperCase() + v.slice(1)).replace(/_/g, ' ').replace(/^Todo$/, 'To do') : '');
